import React, { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import {
  getUserErrorMessage,
  researchAPI,
  ResearchListResponse,
} from '../api/api';
import '../styles/research.css';


const PAGE_SIZE = 12;

function categoryLabel(category: string): string {
  return category
    .split(/[-_ ]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const Research: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState<ResearchListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    researchAPI
      .categories()
      .then((items) => {
        if (active) {
          setCategories(items);
        }
      })
      .catch(() => {
        // The paper list remains usable if category discovery fails.
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const fromUrl = searchParams.get('category');
    if (fromUrl) {
      setCategory(fromUrl);
      setOffset(0);
    }
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    researchAPI
      .list({
        category: category || undefined,
        q: query || undefined,
        limit: PAGE_SIZE,
        offset,
      })
      .then((response) => {
        if (active) {
          setResult(response);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(
            getUserErrorMessage(
              err,
              'The research library could not be loaded. Please try again.'
            )
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [category, query, offset]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    setOffset(0);
    setQuery(searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput('');
    setQuery('');
    setCategory('');
    setOffset(0);
  };

  const total = result?.total ?? 0;
  const shownFrom = total === 0 ? 0 : offset + 1;
  const shownTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <motion.main
      className="container research-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <section className="card research-intro">
        <p className="research-eyebrow">Curated evidence library</p>
        <h1>Research papers</h1>
        <p>
          Browse the studies used by WisemenResearch and open each paper
          from its publisher, DOI, or authorized public source.
        </p>
      </section>

      <section className="card research-controls" aria-label="Research filters">
        <form className="research-search" onSubmit={handleSearch}>
          <label className="form-label" htmlFor="research-search">
            Search papers
          </label>
          <div className="research-search-row">
            <input
              id="research-search"
              className="form-input"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search titles, authors, or abstracts"
              maxLength={200}
            />
            <button className="btn btn-primary" type="submit">
              Search
            </button>
          </div>
        </form>

        <div className="research-category">
          <label className="form-label" htmlFor="research-category">
            Category
          </label>
          <select
            id="research-category"
            className="form-input"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">All categories</option>
            {categories.map((item) => (
              <option key={item} value={item}>
                {categoryLabel(item)}
              </option>
            ))}
          </select>
        </div>

        {(category || query) && (
          <button className="research-clear" type="button" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </section>

      {error && (
        <div className="error research-error" role="alert">
          {error}
        </div>
      )}

      <section aria-live="polite" aria-busy={loading}>
        <div className="research-summary">
          <span>
            {loading ? 'Loading research…' : `${total} paper${total === 1 ? '' : 's'}`}
          </span>
          {!loading && total > 0 && (
            <span>
              Showing {shownFrom}–{shownTo}
            </span>
          )}
        </div>

        {!loading && !error && result?.items.length === 0 && (
          <div className="card research-empty">
            <h2>No matching papers</h2>
            <p>Try another search or clear the category filter.</p>
          </div>
        )}

        <div className="research-grid">
          {result?.items.map((paper) => (
            <article className="card research-card" key={paper.id}>
              <div className="research-card-meta">
                <span className="research-badge">{categoryLabel(paper.category)}</span>
                {paper.publication_year && <span>{paper.publication_year}</span>}
              </div>
              <h2>{paper.title}</h2>
              {paper.authors && <p className="research-authors">{paper.authors}</p>}
              {paper.abstract && <p className="research-abstract">{paper.abstract}</p>}
              <div className="research-card-footer">
                <span>{paper.page_count} pages</span>
                {paper.source_url ? (
                  <a
                    className="research-source"
                    href={paper.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open paper
                  </a>
                ) : (
                  <span className="research-source-missing">Source link unavailable</span>
                )}
              </div>
            </article>
          ))}
        </div>

        {!loading && total > PAGE_SIZE && (
          <nav className="research-pagination" aria-label="Research pagination">
            <button
              className="btn btn-secondary"
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </nav>
        )}
      </section>
    </motion.main>
  );
};

export default Research;
